import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  Mail,
  Phone,
  MapPin,
  Send,
  CheckCircle,
  Clock,
  MessageSquare,
} from 'lucide-react';
import './Contact.css';

const fadeUp = {
  hidden: { opacity: 0, y: 32 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, delay: i * 0.1, ease: [0.22, 1, 0.36, 1] },
  }),
};

const services = [
  'Cloud Solutions',
  'Cybersecurity',
  'AI & Machine Learning',
  'Custom Software',
  'Managed IT',
  'IT Consulting',
];

export default function Contact() {
  const [form, setForm] = useState({
    name: '',
    email: '',
    company: '',
    service: '',
    message: '',
  });
  const [submitStatus, setSubmitStatus] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleChange = (e) =>
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));

  const handleSubmit = (e) => {
    e.preventDefault();
    setLoading(true);
    
    // Construct WhatsApp message
    const message = `Hi! I would like to inquire about your services.
Name: ${form.name}
Email: ${form.email}
Company: ${form.company}
Service of Interest: ${form.service}
Message: ${form.message}`;

    const encodedMessage = encodeURIComponent(message);
    const whatsappUrl = `https://wa.me/918287999236?text=${encodedMessage}`;
    
    window.open(whatsappUrl, '_blank');
    
    setSubmitStatus('success');
    setForm({ name: '', email: '', company: '', service: '', message: '' });
    setLoading(false);
  };

  const contactDetails = [
    {
      icon: <Phone size={18} />,
      label: 'Phone',
      value: '+91 8287999236',
      href: 'tel:+918287999236',
    },
    {
      icon: <Mail size={18} />,
      label: 'Email',
      value: 'wayugpl@gmail.com',
      href: 'mailto:wayugpl@gmail.com',
    },
    {
      icon: <MapPin size={18} />,
      label: 'Location',
      value: 'Connaught Place, New Delhi',
      href: null,
    },
  ];

  return (
    <section id="contact" className="contact">
      <script type="application/ld+json">
        {JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'ContactPage',
          name: 'Contact Wayug Infotech Solutions',
          description:
            'Get in touch with Wayug Infotech Solutions for enterprise IT services including Cloud, Cybersecurity, AI, and Custom Software Development.',
          url: 'https://wayug.com/contact',
          mainEntity: {
            '@type': 'Organization',
            '@id': 'https://wayug.com/#organization',
            name: 'Wayug Infotech Solutions',
            telephone: '+91-8287999236',
            email: 'wayugpl@gmail.com',
            address: {
              '@type': 'PostalAddress',
              streetAddress: 'Connaught Place',
              addressLocality: 'New Delhi',
              addressRegion: 'Delhi',
              postalCode: '110001',
              addressCountry: 'IN',
            },
          },
        })}
      </script>

      <div className="contact-bg-orb contact-orb-1" />
      <div className="contact-bg-orb contact-orb-2" />

      <div className="container">
        <motion.div
          className="section-header"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-80px' }}
        >
          <motion.div className="section-label" variants={fadeUp} custom={0}>
            <MessageSquare size={13} />
            GET IN TOUCH
          </motion.div>
          <motion.h2 variants={fadeUp} custom={1}>
            Start Your{' '}
            <span className="gradient-text">Digital Journey</span>
          </motion.h2>
        </motion.div>

        <div className="contact-layout">
          {/* LEFT — Info */}
          <motion.div
            className="contact-info"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-60px' }}
          >
            <motion.div className="info-intro" variants={fadeUp} custom={0}>
              <h3>Let's build something amazing together.</h3>
              <p>
                Whether you're looking to modernise your infrastructure, secure
                your enterprise, or accelerate product development — our team of
                experts is ready to make it happen.
              </p>
            </motion.div>

            <address className="contact-details" style={{ fontStyle: 'normal' }}>
              {contactDetails.map((item) => (
                <div className="contact-detail-item" key={item.label}>
                  <div className="detail-icon" aria-hidden="true">{item.icon}</div>
                  <div className="detail-text">
                    <span className="detail-label">{item.label}</span>
                    {item.href ? (
                      <a className="detail-value" href={item.href}>
                        {item.value}
                      </a>
                    ) : (
                      <span className="detail-value">{item.value}</span>
                    )}
                  </div>
                </div>
              ))}
            </address>

            <motion.div className="response-time" variants={fadeUp} custom={2}>
              <Clock size={16} />
              <span>We respond within&nbsp;<strong>4 hours</strong></span>
            </motion.div>

            <motion.div className="contact-services" variants={fadeUp} custom={3}>
              <h4>What we can help with:</h4>
              <div className="service-pills">
                {services.map((s) => (
                  <span className="service-pill" key={s}>
                    {s}
                  </span>
                ))}
              </div>
            </motion.div>
          </motion.div>

          {/* RIGHT — Form */}
          <motion.div
            className="contact-form-wrap"
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-60px' }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1], delay: 0.15 }}
          >
            {submitStatus !== 'success' ? (
              <form className="contact-form" onSubmit={handleSubmit} noValidate>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="name">Full Name</label>
                    <input
                      id="name"
                      name="name"
                      type="text"
                      placeholder="John Smith"
                      value={form.name}
                      onChange={handleChange}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="email">Email Address</label>
                    <input
                      id="email"
                      name="email"
                      type="email"
                      placeholder="john@company.com"
                      value={form.email}
                      onChange={handleChange}
                      required
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="company">Company Name</label>
                  <input
                    id="company"
                    name="company"
                    type="text"
                    placeholder="Your Organisation"
                    value={form.company}
                    onChange={handleChange}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="service">Service Required</label>
                  <select
                    id="service"
                    name="service"
                    className="service-select"
                    value={form.service}
                    onChange={handleChange}
                  >
                    <option value="">Select a service…</option>
                    <option value="Cloud Solutions">Cloud Solutions</option>
                    <option value="Cybersecurity">Cybersecurity</option>
                    <option value="AI & Machine Learning">AI &amp; Machine Learning</option>
                    <option value="Custom Software">Custom Software</option>
                    <option value="Managed IT">Managed IT</option>
                    <option value="IT Consulting">IT Consulting</option>
                    <option value="Other">Other</option>
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="message">Message</label>
                  <textarea
                    id="message"
                    name="message"
                    className="message-input"
                    rows={6}
                    placeholder="Tell us about your project, challenges, or questions…"
                    value={form.message}
                    onChange={handleChange}
                    required
                  />
                </div>

                {submitStatus === 'error' && (
                  <div className="error-message">
                    <span>Something went wrong. Please try again or email us directly.</span>
                  </div>
                )}

                <button
                  type="submit"
                  className="btn btn-accent submit-btn"
                  disabled={loading}
                >
                  {loading ? (
                    <span className="btn-spinner" />
                  ) : (
                    <>
                      <Send size={16} />
                      Send Message
                    </>
                  )}
                </button>
              </form>
            ) : (
              <motion.div
                className="success-message"
                initial={{ opacity: 0, scale: 0.92 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
              >
                <CheckCircle size={28} />
                <div>
                  <strong>Message sent!</strong>
                  <p>Thank you for reaching out. We'll get back to you within 4 hours.</p>
                </div>
              </motion.div>
            )}
          </motion.div>
        </div>
      </div>
    </section>
  );
}
